/**
 * UuidRecordsList Component
 * Displays detailed list of UUID-format metric records
 *
 * @module MetricDataDevTool/components/UuidRecordsList
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { Error as UuidIcon } from '@mui/icons-material';
import { format } from 'date-fns';

/**
 * UuidRecordsList component
 * @param {Object} props - Component props
 * @param {Array} props.records - Array of UUID-format records
 */
export default function UuidRecordsList({ records = [] }) {
  if (records.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No UUID records found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <UuidIcon color="warning" fontSize="small" />
        <Typography variant="subtitle2" fontWeight="bold">
          Legacy UUID Records ({records.length})
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These records use the old UUID format instead of the composite key structure.
        They may be duplicates or legacy data from migration.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        {records.map((record, index) => (
          <Box
            key={record.metricId || index}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'warning.light',
              borderRadius: 1,
              backgroundColor: 'warning.lighter'
            }}
          >
            <Stack spacing={1}>
              {/* MetricId */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Metric ID (UUID):
                </Typography>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {record.metricId}
                </Typography>
              </Box>

              {/* Record Details */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={`Store: ${record.storeId}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Date: ${record.date}`}
                  size="small"
                  variant="outlined"
                />
                {record.dayPart && (
                  <Chip
                    label={`Day Part: ${record.dayPart}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {record.diningOption && (
                  <Chip
                    label={`Dining: ${record.diningOption}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              {/* Created At */}
              {record.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  Created: {format(new Date(record.createdAt), 'MMM d, yyyy h:mm a')}
                </Typography>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
